import React, { useState } from "react";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { FaDownload, FaEdit } from "react-icons/fa";
import { HiThumbUp, HiThumbDown } from "react-icons/hi";

function ImageCard({ image, imgIndex, onDetails }) {
  const [isLiked, setIsLiked] = useState(false); // State for like
  const [isUnliked, setIsUnliked] = useState(false);

  // Toggle like
  const handleLike = () => {
    setIsLiked(true);
    setIsUnliked(false);
    console.log("Liked:", image.url);
  };

  const handleUnlike = () => {
    setIsUnliked(true);
    setIsLiked(false);
    console.log("Unliked:", image.url);
  };

  // Handle edit click
  const handleEditClick = () => {
    console.log("Edit clicked");
    if (onDetails) {
      onDetails(image);
    }
  };

  // Handle download
  const handleDownload = async () => {
    try {
      const response = await fetch(image.url);
      
      // Get the content type from response headers, fallback to detecting from URL
      let contentType = response.headers.get('content-type');
      
      // If content-type is not available, try to detect from URL
      if (!contentType) {
        const urlLower = image.url.toLowerCase();
        if (urlLower.includes('.png')) {
          contentType = 'image/png';
        } else if (urlLower.includes('.webp')) {
          contentType = 'image/webp';
        } else if (urlLower.includes('.gif')) {
          contentType = 'image/gif';
        } else {
          contentType = 'image/jpeg'; // Default to JPEG
        }
      }
      
      // Ensure we have a valid image MIME type
      if (!contentType.startsWith('image/')) {
        contentType = 'image/jpeg';
      }
      
      // Create blob with explicit MIME type to ensure thumbnail generation
      const blob = await response.blob();
      // Create a new blob with explicit type to ensure OS recognizes it correctly
      const typedBlob = blob.type === contentType 
        ? blob 
        : new Blob([blob], { type: contentType });
      
      // Determine file extension based on content type
      let extension = 'jpg';
      if (contentType.includes('png')) {
        extension = 'png';
      } else if (contentType.includes('webp')) {
        extension = 'webp';
      } else if (contentType.includes('gif')) {
        extension = 'gif';
      }
      
      // Generate a more descriptive filename (frontend-controlled)
      // Format: ambaram-ai-image-YYYY-MM-DD-HHMMSS-index.jpg
      const now = new Date();
      const timestamp = now.toISOString().replace(/[:.]/g, '-').slice(0, -5);
      const fileName = `ambaram-ai-image-${timestamp}-${imgIndex + 1}.${extension}`;
      
      const url = window.URL.createObjectURL(typedBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      a.style.display = 'none'; // Hide the link element
      document.body.appendChild(a);
      a.click();
      
      // Clean up after a short delay to ensure download starts
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }, 100);
      
      console.log("Image downloaded successfully:", fileName, "Type:", contentType);
    } catch (error) {
      console.error("Failed to download image:", error);
    }
  };

  const containerStyle = {
    position: "relative",
    display: "inline-block",
    marginBottom: "1rem",
    overflow: "hidden",
  };

  const placeholderStyle = {
    width: "240px",
    height: "240px",
    borderRadius: "0.8rem",
  };

  const imageStyle = {
    width: "240px",
    height: "auto",
    objectFit: "cover",
    borderRadius: "0.8rem",
    transition: "transform 0.3s ease-in-out",
  };

  const iconContainerStyle = {
    position: "absolute",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "transform 0.3s ease-in-out, opacity 0.3s ease-in-out",
    opacity: 0,
  };

  // Style for individual icon button wrapper
  const iconButtonStyle = {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "36px",
    height: "36px",
    borderRadius: "50%",
    backgroundColor: "rgba(0, 0, 0, 0.6)",
    backdropFilter: "blur(8px)",
    WebkitBackdropFilter: "blur(8px)",
    cursor: "pointer",
    transition: "all 0.2s ease-in-out",
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.3)",
  };

  const bottomIconsStyle = {
    ...iconContainerStyle,
    bottom: "-1.5rem",
    right: "0.5rem",
    flexDirection: "row",
    gap: "0.5rem",
  };

  const topRightIconsStyle = {
    ...iconContainerStyle,
    top: "0.5rem",
    right: "0.5rem",
    flexDirection: "row",
    gap: "0.5rem",
    transform: "translateY(0)", // Prevent moving up on hover
  };

  const isLoadingImage = !image?.url;

  // Render a loading card when the image URL is not yet available
  if (isLoadingImage) {
    return (
      <figure style={containerStyle}>
        <div style={placeholderStyle} className="aitool-image-loader">
          <Skeleton
            width={240}
            height={240}
            borderRadius={12}
            style={{ display: "block" }}
          />
        </div>
      </figure>
    );
  }

  return (
    <figure
      style={containerStyle}
      onMouseEnter={(e) => {
        const icons = e.currentTarget.querySelectorAll(".icon");
        icons.forEach((icon) => {
          icon.style.opacity = "1";
          // Only move bottom icons up, keep top icons in place
          if (icon.style.top === "0.5rem") {
            icon.style.transform = "translateY(0)"; // Keep download button in place
          } else {
            icon.style.transform = "translateY(-2rem)"; // Move bottom icons up
          }
        });
      }}
      onMouseLeave={(e) => {
        const icons = e.currentTarget.querySelectorAll(".icon");
        icons.forEach((icon) => {
          icon.style.opacity = "0";
          icon.style.transform = "translateY(0)";
        });
      }}
    >
      <img src={image.url} alt={`Generated ${imgIndex}`} style={imageStyle} />
      
      {/* Top right - Download button */}
      <div className="icon" style={topRightIconsStyle}>
        <div
          style={iconButtonStyle}
          onClick={handleDownload}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = "rgba(0, 0, 0, 0.8)";
            e.currentTarget.style.transform = "scale(1.1)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = "rgba(0, 0, 0, 0.6)";
            e.currentTarget.style.transform = "scale(1)";
          }}
        >
          <FaDownload
            style={{
              fontSize: "1.3rem",
              color: "white",
              filter: "drop-shadow(0 1px 2px rgba(0, 0, 0, 0.8))",
            }}
          />
        </div>
      </div>

      {/* Bottom right - Like, Dislike, and Edit */}
      <div className="icon" style={bottomIconsStyle}>
        <div
          style={iconButtonStyle}
          onClick={handleLike}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = "rgba(0, 0, 0, 0.8)";
            e.currentTarget.style.transform = "scale(1.1)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = "rgba(0, 0, 0, 0.6)";
            e.currentTarget.style.transform = "scale(1)";
          }}
        >
          <HiThumbUp
            style={{
              fontSize: "1.3rem",
              color: isLiked ? "#ff4444" : "white",
              filter: "drop-shadow(0 1px 2px rgba(0, 0, 0, 0.8))",
            }}
          />
        </div>
        <div
          style={iconButtonStyle}
          onClick={handleUnlike}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = "rgba(0, 0, 0, 0.8)";
            e.currentTarget.style.transform = "scale(1.1)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = "rgba(0, 0, 0, 0.6)";
            e.currentTarget.style.transform = "scale(1)";
          }}
        >
          <HiThumbDown
            style={{
              fontSize: "1.3rem",
              color: isUnliked ? "#ff4444" : "white",
              filter: "drop-shadow(0 1px 2px rgba(0, 0, 0, 0.8))",
            }}
          />
        </div>
        <div
          style={iconButtonStyle}
          onClick={handleEditClick}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = "rgba(0, 0, 0, 0.8)";
            e.currentTarget.style.transform = "scale(1.1)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = "rgba(0, 0, 0, 0.6)";
            e.currentTarget.style.transform = "scale(1)";
          }}
        >
          <FaEdit
            style={{
              fontSize: "1.3rem",
              color: "white",
              filter: "drop-shadow(0 1px 2px rgba(0, 0, 0, 0.8))",
            }}
          />
        </div>
      </div>
    </figure>
  );
}

export default ImageCard;
